import { Alert, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { useState, type FormEvent } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import * as adminEndpoints from '../api/admin/endpoints';
import * as businessEndpoints from '../api/business/endpoints';
import type { SessionKind } from '../api/SessionKind';
import { audienceOfKind, kindOfAudience } from '../components/audience';
import { KindToggle } from '../components/KindToggle';
import { PublicPage } from '../components/PublicPage';
import { publicErrorMessage } from '../components/publicErrorMessage';
import { useT } from '../i18n/LanguageContext';

/** "Forgot password": the API mails a link, and says the same whether or not the e-mail exists (R0005 D-06). */
export const ForgotPasswordPage = () => {
  const t = useT();
  const [searchParams] = useSearchParams();
  const [kind, setKind] = useState<SessionKind>(kindOfAudience(searchParams.get('audience')));
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setIsBusy(true);

    try {
      const trimmed = email.trim();

      await (kind === 'platform'
        ? adminEndpoints.requestPasswordReset(trimmed)
        : businessEndpoints.requestPasswordReset(trimmed));
      setSentTo(trimmed);
    } catch (caught) {
      setError(publicErrorMessage(caught, t));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <PublicPage>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {t('forgot.title')}
      </Typography>
      {sentTo ? (
        <Stack spacing={2}>
          <Alert severity="success" variant="outlined">
            {t('forgot.sent', { email: sentTo })}
          </Alert>
          <Button component={RouterLink} to={`/login?audience=${audienceOfKind(kind)}`} variant="outlined">
            {t('forgot.back')}
          </Button>
        </Stack>
      ) : (
        <>
          <KindToggle value={kind} onChange={setKind} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.55 }}>
            {t('forgot.hint')}
          </Typography>
          <form onSubmit={submit}>
            <Stack spacing={2}>
              {error ? (
                <Alert severity="error" variant="outlined">
                  {error}
                </Alert>
              ) : null}
              <TextField
                label={t('field.email')}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                autoFocus
                required
                fullWidth
              />
              <Button type="submit" variant="contained" size="large" disabled={isBusy}>
                {isBusy ? t('forgot.busy') : t('forgot.submit')}
              </Button>
              <Link component={RouterLink} to={`/login?audience=${audienceOfKind(kind)}`} variant="body2">
                {t('forgot.back')}
              </Link>
            </Stack>
          </form>
        </>
      )}
    </PublicPage>
  );
};
